import { useCallback, useEffect, useState } from "react";
import { BellRing, Loader2, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtUsd } from "@/lib/api";

export const PriceAlerts = ({ price }) => {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const target = parseFloat(val.replace(/,/g, "")) || 0;
  const onChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const [int, ...dec] = raw.split(".");
    const intFmt = int ? Number(int).toLocaleString("en-US") : "";
    setVal(dec.length ? `${intFmt || "0"}.${dec.join("").slice(0, 2)}` : intFmt);
  };

  const load = useCallback(() => api.get("/alerts").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts", { target_price: target });
      toast.success(`Alert set: BTC ${data.direction} ${fmtUsd(data.target_price)}`);
      setVal("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = (id) => api.delete(`/alerts/${id}`).then(load).catch((e) => toast.error(errMsg(e)));

  return (
    <div data-testid="price-alerts-card">
      <div className="mb-4 flex items-center gap-2"><BellRing size={18} className="text-[#F7931A]" /><h3 className="text-lg font-semibold tracking-tight">Price alerts</h3></div>
      <p className="mb-3 text-xs text-[#94A3B8]">Get an in-app and email alert when BTC crosses your price.</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#64748B]">$</span>
          <input value={val} onChange={onChange} type="text" inputMode="decimal" placeholder={price ? Math.round(price * 1.05).toLocaleString("en-US") : "Target price"}
            data-testid="alert-price-input" className="w-full rounded-xl border border-[#212638] bg-[#0F111A] py-2.5 pl-7 pr-3 font-mono outline-none transition-colors focus:border-[#F7931A]" />
        </div>
        <button onClick={add} disabled={busy || target <= 0} data-testid="alert-create-button"
          className="flex items-center gap-1.5 rounded-xl bg-[#F7931A] px-4 text-sm font-semibold text-[#08090E] transition-colors hover:bg-amber-500 disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Set alert
        </button>
      </div>
      {target > 0 && price && <p className="mt-1.5 text-xs text-[#64748B]" data-testid="alert-direction-hint">Alerts when BTC goes {target > price ? "above" : "below"} {fmtUsd(target)}</p>}
      <div className="mt-4 space-y-2" data-testid="alert-list">
        {items.length === 0 && <p className="py-4 text-center text-xs text-[#64748B]">No alerts yet.</p>}
        {items.map((a) => (
          <div key={a.alert_id} className="flex items-center justify-between rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2" data-testid={`alert-item-${a.alert_id}`}>
            <span className="flex items-center gap-2 text-sm">
              {a.direction === "above" ? <TrendingUp size={14} className="text-emerald-400" /> : <TrendingDown size={14} className="text-red-400" />}
              <span className="text-[#94A3B8]">{a.direction}</span><span className="font-mono font-semibold">{fmtUsd(a.target_price)}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${a.status === "active" ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"}`}>{a.status}</span>
              <button onClick={() => remove(a.alert_id)} data-testid={`alert-delete-${a.alert_id}`} className="text-[#64748B] transition-colors hover:text-red-400"><Trash2 size={14} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
